import { inject, injectable } from 'inversify'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { RouterRepository } from '@core/routing/router.repository'
import type { RouteId } from '@core/routing/router.config'
import type { RouteTransition } from '@core/routing/route-transition'
import type { UnsavedWork } from '@core/interfaces/unsaved-work'

/**
 * One key per **form**, in the same spirit as FeedbackScopes and FilterScopes:
 * the mechanism is keyed, so a form gains unsaved-work protection by naming
 * itself here rather than by implementing anything.
 */
export const DraftKeys = [
  'workspaces.create',
  'workspaces.rename',
  'workspaces.invite',
  'jobs.create',
  'jobs.rename',
  'jobs.recurrence',
  'procedures.create',
  'procedures.edit',
  'locations.create',
  'locations.edit',
  'taskTypes.create',
  'assets.rename',
  'assets.add',
  'teams.create',
  'teams.rename',
  'teams.member',
  'admin.teams.create',
  'admin.teams.member',
  'admin.users.password',
  'admin.users.create',
  'admin.grants.grant',
] as const
export type DraftKey = (typeof DraftKeys)[number]

type DraftFields = Readonly<Record<string, string>>

/**
 * What the user has typed and not yet saved, for every form in the app.
 *
 * **Why the values live here and not on the Presenter that renders them.** The
 * app already had the whole unsaved-work mechanism — `Types.IUnsavedWork`, the
 * `beforeunload` warning, `LeaveConfirmationService` and its two-phase discard —
 * with exactly one participant: the infra demo. No real form was protected, and
 * the reason none could be is lifetime (P-28). A Presenter unmounts when its
 * pane closes or its route changes, which is precisely when the question "is
 * there unsaved work?" needs answering, and precisely when a Presenter-held
 * answer has already been destroyed. Worse, "Stay" has to mean the typed text is
 * still there — which it cannot be if closing the surface threw it away.
 *
 * So this is one Repository holding every draft, and it is the single
 * `IUnsavedWork` participant. A new form protects itself by adding a key above
 * and reading its value through here; nothing else changes, anywhere.
 *
 * User-scoped for the ordinary reason: half-typed text belongs to whoever typed
 * it, and the next login must not find it waiting.
 */
@injectable()
export class DraftRepository implements UnsavedWork {
  /** Key → field → value. A field is absent until typed into. */
  draftsPm: Readonly<Partial<Record<DraftKey, DraftFields>>> = {}

  /**
   * What each form was opened *with*, so "dirty" means changed rather than
   * non-empty. A rename form opens holding the current name; without a baseline
   * it would be born dirty and every close would ask about work nobody did.
   */
  private baselineByKey: Partial<Record<DraftKey, DraftFields>> = {}

  /**
   * Which route each draft was typed on, so a navigation between two *other*
   * routes does not raise a prompt about work it would not lose. Recorded here
   * rather than declared per form: a form knows its own fields, not its place in
   * the route table, and asking it to would put routing knowledge in six places.
   */
  private routeByKey: Partial<Record<DraftKey, RouteId | null>> = {}

  private readonly routerRepository: RouterRepository

  constructor(@inject(RouterRepository) routerRepository: RouterRepository) {
    this.routerRepository = routerRepository

    makeObservable(this, {
      draftsPm: observable.ref,
      open: action,
      setField: action,
      clear: action,
    })
  }

  field = (key: DraftKey, field: string): string => this.draftsPm[key]?.[field] ?? ''

  /** True when what is in the form differs from what it was opened with. */
  isDirty = (key: DraftKey): boolean => {
    const values = this.draftsPm[key]
    if (values === undefined) return false

    const baseline = this.baselineByKey[key] ?? {}
    return Object.keys(values).some((field) => (values[field] ?? '') !== (baseline[field] ?? ''))
  }

  /** A form opening, with whatever it is prefilled with (nothing, usually). */
  open = (key: DraftKey, initial: DraftFields = {}): void => {
    this.baselineByKey[key] = initial
    this.routeByKey[key] = this.routerRepository.currentRoute.routeId
    this.draftsPm = { ...this.draftsPm, [key]: initial }
  }

  setField = (key: DraftKey, field: string, value: string): void => {
    // Also here, not only in `open`: a form that is simply always on the page
    // (the create bar above a list) is never opened, and a draft with no route
    // recorded would be stranded silently by the next navigation.
    this.routeByKey[key] = this.routerRepository.currentRoute.routeId
    this.draftsPm = { ...this.draftsPm, [key]: { ...this.draftsPm[key], [field]: value } }
  }

  /** Saved, cancelled, or discarded — all three end the same way. */
  clear = (key: DraftKey): void => {
    const rest = { ...this.draftsPm }
    delete rest[key]
    delete this.routeByKey[key]
    delete this.baselineByKey[key]
    this.draftsPm = rest
  }

  private dirtyKeys(): DraftKey[] {
    return (Object.keys(this.draftsPm) as DraftKey[]).filter((key) => this.isDirty(key))
  }

  // --- UnsavedWork -----------------------------------------------------------

  hasUnsavedWork(): boolean {
    return this.dirtyKeys().length > 0
  }

  /**
   * Only the drafts the transition would actually strand. A draft typed on the
   * page being left is lost; one typed on a third page is not, and prompting
   * about it is the false alarm `isAffectedBy` exists to prevent.
   */
  isAffectedBy(transition: RouteTransition): boolean {
    return this.dirtyKeys().some((key) => {
      const owner = this.routeByKey[key]
      // `from` is null on the session's very first navigation, and so is the
      // owner of a draft typed before any route committed. Matching those two
      // nulls would read as "same page" when neither is a page at all.
      return owner !== null && owner === transition.from
    })
  }

  discardUnsavedWork(): void {
    runInAction(() => {
      this.draftsPm = {}
      this.routeByKey = {}
      this.baselineByKey = {}
    })
  }

  resetUserScopedState = (): void => {
    this.discardUnsavedWork()
  }
}
